import { cva, type VariantProps } from 'class-variance-authority';
import { type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

// ---------------------------------------------------------------------------
// Badge
//
// Small inline status pill - mono uppercase, tonal background. The Discord
// connection card uses tones info / warning / teal to telegraph Pending /
// Needs attention / Connected; the same component will absorb future status
// pills (build markers, milestone state, etc) without the call sites
// hand-rolling identical Tailwind class strings.
// ---------------------------------------------------------------------------

export const badgeVariants = cva(
  [
    'inline-flex shrink-0 items-center whitespace-nowrap',
    'rounded-sm border p-[0.4rem]',
    'font-mono text-[10px] uppercase tracking-wider',
    // leading-none keeps the height equal to text + py, otherwise the
    // browser default line-height nudges the pill ~4px taller than the
    // visible glyph row.
    'leading-none',
  ].join(' '),
  {
    variants: {
      tone: {
        neutral: 'bg-surface-2 border-border text-text-muted',
        info: 'bg-info-bg border-info text-info-text',
        success: 'bg-teal-bg border-teal text-teal-text',
        warning: 'bg-warning-bg border-warning text-warning-text',
        danger: 'bg-danger-bg border-danger text-danger-text',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps): JSX.Element {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}
